// frontend/src/pages/EditAd.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import p2pAxios from '../p2pAxios';

const COIN_OPTIONS = ['USDT', 'BTC', 'ETH', 'USDC'];
const EXPIRE_ALLOWED = [15, 30, 45, 60, 90, 180];

const EditAd = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    coin: 'USDT',
    price: '',
    minLimit: '',
    maxLimit: '',
    terms: '',
    autoReply: '',
    expireMinutes: '',
    paymentMethods: [],
  });

  const paymentOptions = ['bank', 'upi', 'paytm', 'phonepe', 'gpay'];

  const fetchAd = async () => {
    try {
      const res = await p2pAxios.get(`/ads/single/${id}`);
      const ad = res.data.ad;
      setForm({
        coin: (ad.coin || 'USDT').toUpperCase(),
        price: ad.price ?? '',
        minLimit: ad.minLimit ?? '',
        maxLimit: ad.maxLimit ?? '',
        terms: ad.terms ?? '',
        autoReply: ad.autoReply ?? '',
        expireMinutes: ad.expireMinutes ?? '',
        paymentMethods: Array.isArray(ad.paymentMethods) ? ad.paymentMethods : [],
      });
    } catch (err) {
      console.error('Failed to fetch ad:', err);
      setError('Failed to load ad. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAd(); }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const togglePaymentMethod = (method) => {
    setForm((prev) => {
      const selected = new Set(prev.paymentMethods || []);
      if (selected.has(method)) selected.delete(method);
      else selected.add(method);
      return { ...prev, paymentMethods: Array.from(selected) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!form.price || !form.minLimit || !form.maxLimit || !form.terms) {
      setError('Please fill in price, limits and terms.');
      return;
    }
    if (Number(form.minLimit) > Number(form.maxLimit)) {
      setError('Minimum limit cannot be greater than maximum limit.');
      return;
    }
    if (!COIN_OPTIONS.includes(form.coin)) {
      setError('Invalid coin selected.');
      return;
    }
    if (form.expireMinutes !== '' && !EXPIRE_ALLOWED.includes(Number(form.expireMinutes))) {
      setError('Invalid trade expiry.');
      return;
    }

    try {
      const payload = {
        coin: form.coin,
        price: Number(form.price),
        minLimit: Number(form.minLimit),
        maxLimit: Number(form.maxLimit),
        terms: form.terms,
        autoReply: form.autoReply,
        paymentMethods: form.paymentMethods,
        expireMinutes: form.expireMinutes === '' ? undefined : Number(form.expireMinutes),
      };

      await p2pAxios.put(`/ads/edit/${id}`, payload);
      setMessage('Ad updated successfully!');
      setTimeout(() => navigate('/p2p'), 1200);
    } catch (err) {
      console.error('Failed to update ad:', err);
      setError(err?.response?.data?.message || 'Update failed. Please try again.');
    }
  };

  if (loading) return <p className="text-center text-gray-600">Loading ad...</p>;

  return (
    <div className="max-w-xl mx-auto md:mt-10 mt-4 md:px-0 px-3">
      <div className="bg-white md:rounded-xl rounded-lg md:shadow-md shadow border-t-4 border-blue-600 md:p-6 p-4">
        <h2 className="md:text-xl text-lg font-semibold md:mb-4 mb-3 text-gray-800">Edit P2P Ad</h2>

        {message && (
          <div className="mb-3 md:mb-4 md:p-3 p-2.5 bg-green-100 text-green-800 border border-green-300 rounded-lg text-sm">
            {message}
          </div>
        )}
        {error && (
          <div className="mb-3 md:mb-4 md:p-3 p-2.5 bg-red-100 text-red-800 border border-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="md:space-y-4 space-y-3">
          {/* Coin */}
          <div>
            <label className="block md:text-sm text-[12px] font-medium text-gray-700 mb-1">Coin</label>
            <select
              name="coin"
              value={form.coin}
              onChange={handleInputChange}
              className="w-full md:p-2 p-2 border rounded-md md:text-sm text-[13px]"
            >
              {COIN_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div>
            <label className="block md:text-sm text-[12px] font-medium text-gray-700">Price (₹ per {form.coin})</label>
            <input
              type="number"
              name="price"
              value={form.price}
              onChange={handleInputChange}
              className="mt-1 w-full md:p-2 p-2 border rounded-md md:text-sm text-[13px]"
              required
            />
          </div>

          {/* Limits */}
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 gap-2.5">
            <div>
              <label className="block md:text-sm text-[12px] font-medium text-gray-700">Min Limit (₹)</label>
              <input
                type="number"
                name="minLimit"
                value={form.minLimit}
                onChange={handleInputChange}
                className="mt-1 w-full md:p-2 p-2 border rounded-md md:text-sm text-[13px]"
                required
              />
            </div>
            <div>
              <label className="block md:text-sm text-[12px] font-medium text-gray-700">Max Limit (₹)</label>
              <input
                type="number"
                name="maxLimit"
                value={form.maxLimit}
                onChange={handleInputChange}
                className="mt-1 w-full md:p-2 p-2 border rounded-md md:text-sm text-[13px]"
                required
              />
            </div>
          </div>

          {/* Expiry */}
          <div>
            <label className="block md:text-sm text-[12px] font-medium text-gray-700 mb-1">Trade expiry (minutes)</label>
            <select
              name="expireMinutes"
              value={form.expireMinutes === '' ? '' : String(form.expireMinutes)}
              onChange={handleInputChange}
              className="w-full md:p-2 p-2 border rounded-md md:text-sm text-[13px]"
            >
              <option value="">Platform default</option>
              {EXPIRE_ALLOWED.map((m) => (
                <option key={m} value={m}>{m} minutes</option>
              ))}
            </select>
          </div>

          {/* Terms */}
          <div>
            <label className="block md:text-sm text-[12px] font-medium text-gray-700">Terms & Conditions</label>
            <textarea
              name="terms"
              value={form.terms}
              onChange={handleInputChange}
              className="mt-1 w-full md:p-2 p-2 border rounded-md md:text-sm text-[13px]"
              rows={3}
              required
            />
          </div>

          {/* Auto Reply */}
          <div>
            <label className="block md:text-sm text-[12px] font-medium text-gray-700">Auto Reply</label>
            <textarea
              name="autoReply"
              value={form.autoReply}
              onChange={handleInputChange}
              className="mt-1 w-full md:p-2 p-2 border rounded-md md:text-sm text-[13px]"
              rows={3}
              placeholder="Optional"
            />
          </div>

          {/* Payment Methods */}
          <div>
            <label className="block md:text-sm text-[12px] font-medium text-gray-700 md:mb-2 mb-1.5">Payment Methods</label>
            <div className="flex flex-wrap md:gap-2 gap-1.5">
              {paymentOptions.map((method) => {
                const active = (form.paymentMethods || []).includes(method);
                return (
                  <button
                    type="button"
                    key={method}
                    onClick={() => togglePaymentMethod(method)}
                    className={`md:px-3 px-2.5 md:py-1.5 py-1 rounded-md border md:text-sm text-[12px] ${
                      active ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {method.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white md:px-6 px-4 md:py-2 py-2.5 md:text-sm text-[13px] rounded-md w-full md:w-auto"
            >
              Update Ad
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAd;
